import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Clock, Mail, Plus, Search, X } from 'lucide-react';
import { PublicOrganizer, organizerAvatarColor, organizerInitials } from '@radevents/shared';
import api from '../../services/api';
import organizerAccessService from '../../services/organizerAccessService';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganizerPortal } from '../../contexts/OrganizerPortalContext';
import { claimDomainOf, organizerDomainMatches } from './claimIntent';
import { trackOrganizerFunnel } from '../../services/trackingService';
import { PrivacyNote } from '../PrivacyNote';

// ── „Veranstalter werden" in einem Dialog ────────────────────────────────────────────────────
// Bis zum 22.09.2026 führte der Weg von der Angebotsseite über vier Seiten: `/fuer-veranstalter`
// → `/veranstalter` (Liste) → `/veranstalter/:id` → `/login` → zurück zur Veranstalterseite, dort
// erst der Antrag. Jeder Seitenwechsel war eine Stelle zum Aussteigen, und wer sich unterwegs
// anmelden musste, kam mit Bestätigungsmail und Passwort gar nicht mehr zurück.
//
// Hier steht derselbe Ablauf in einem Dialog: Veranstalter suchen oder neu eintragen, E-Mail
// angeben, Zugang beantragen. Es sind dieselben Endpunkte und dieselben Regeln wie vorher — nur
// ohne die Seitenwechsel dazwischen.
//
// Die Anmeldung läuft über den Einmal-Code (`/auth/email/request` + `/verify`). Der beweist den
// Adressbesitz, legt bei Bedarf das Konto an und setzt `emailVerified` — und genau darauf beruht
// die sofortige Freischaltung, wenn die Adresse zur Domain des Veranstalters passt. Ein Konto mit
// Passwort und Bestätigungsmail könnte das nicht: es wäre beim Antrag noch unbestätigt.

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";
const INK = '#2C2C2C';
const MUTED = '#6A6A6A';
const BORDER = '#E5E1E1';
const BERRY = '#922C57';

export const ORGANIZER_ACCESS_CSS = `
.oa-backdrop { position:fixed; inset:0; z-index:60; background:rgba(36,26,31,.55);
  display:flex; align-items:center; justify-content:center; padding:16px; }
.oa-dialog { width:100%; max-width:520px; max-height:92vh; overflow-y:auto; overscroll-behavior:contain;
  box-sizing:border-box; background:#fff; border-radius:16px; padding:20px 24px 24px;
  box-shadow:0 18px 48px rgba(36,26,31,.24); }
.oa-top { display:flex; align-items:center; justify-content:space-between; gap:12px; }
.oa-step { font-family:${FONT_MONO}; font-size:10.5px; letter-spacing:.14em; text-transform:uppercase; color:${BERRY}; }
.oa-icon-btn { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px;
  border-radius:999px; border:1px solid ${BORDER}; background:#fff; color:${MUTED}; cursor:pointer; flex-shrink:0; }
.oa-icon-btn:hover { background:#FBFAFA; color:${INK}; }
.oa-icon-slot { width:32px; height:32px; flex-shrink:0; }
.oa-title { font-family:${FONT_DISPLAY}; font-weight:800; font-size:22px; letter-spacing:-.015em;
  color:${INK}; margin:12px 0 0; }
.oa-lead { font-family:${FONT_BODY}; font-size:14.5px; line-height:1.55; color:${MUTED}; margin:8px 0 0; }
.oa-body { margin-top:18px; display:grid; gap:12px; }
.oa-label { font-family:${FONT_MONO}; font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:#9A9494; }
.oa-input { width:100%; box-sizing:border-box; margin-top:6px; border:1px solid ${BORDER}; border-radius:10px;
  padding:11px 12px; font-family:${FONT_BODY}; font-size:15px; color:${INK}; background:#fff; }
.oa-input:focus { outline:none; border-color:${BERRY}; }
.oa-search { display:flex; align-items:center; gap:9px; border:1px solid ${BORDER}; border-radius:10px;
  padding:11px 12px; background:#fff; }
.oa-search:focus-within { border-color:${BERRY}; }
.oa-search input { flex:1; min-width:0; border:none; outline:none; background:transparent;
  font-family:${FONT_BODY}; font-size:15px; color:${INK}; }
.oa-results { display:grid; gap:8px; max-height:290px; overflow-y:auto; }
.oa-result { display:flex; width:100%; box-sizing:border-box; align-items:center; gap:11px; text-align:left;
  padding:10px 12px; border:1px solid ${BORDER}; border-radius:10px; background:#fff; cursor:pointer; }
.oa-result:hover { border-color:${BERRY}; background:#FFF9FB; }
.oa-result-name { display:block; font-family:${FONT_BODY}; font-weight:700; font-size:14.5px;
  color:${INK}; overflow-wrap:anywhere; }
.oa-result-sub { display:block; font-family:${FONT_BODY}; font-size:13px; color:${MUTED}; margin-top:2px; }
.oa-avatar { width:34px; height:34px; border-radius:999px; flex-shrink:0; color:#fff;
  display:flex; align-items:center; justify-content:center;
  font-family:${FONT_DISPLAY}; font-weight:800; font-size:12.5px; }
.oa-empty { font-family:${FONT_BODY}; font-size:14px; line-height:1.55; color:${MUTED}; }
.oa-missing { border-top:1px solid ${BORDER}; padding-top:14px; font-family:${FONT_BODY};
  font-size:14px; color:${MUTED}; }
.oa-link { background:none; border:none; padding:0; font:inherit; font-weight:700; color:${BERRY};
  cursor:pointer; text-decoration:underline; }
.oa-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; font-family:${FONT_BODY};
  font-weight:700; font-size:15px; padding:12px 20px; border-radius:10px; border:1px solid ${BORDER};
  background:#fff; color:${INK}; cursor:pointer; }
.oa-btn:hover { background:#FBFAFA; }
.oa-btn--primary { background:${BERRY}; border-color:${BERRY}; color:#fff; }
.oa-btn--primary:hover { background:#7E2549; }
.oa-btn[disabled] { opacity:.6; cursor:default; }
.oa-actions { display:flex; gap:10px; flex-wrap:wrap; align-items:center; margin-top:6px; }
.oa-note { font-family:${FONT_BODY}; font-size:13.5px; line-height:1.5; border-radius:10px; padding:10px 12px;
  background:rgba(31,138,91,.09); color:#1B6B48; }
.oa-hint { font-family:${FONT_BODY}; font-size:13px; line-height:1.5; color:${MUTED}; }
.oa-picked { display:flex; align-items:center; gap:11px; border:1px solid ${BORDER}; border-radius:10px;
  padding:10px 12px; background:#FBFAFA; }
.oa-code { width:100%; box-sizing:border-box; border:1px solid ${BORDER}; border-radius:10px; padding:13px 12px;
  font-family:${FONT_MONO}; font-size:22px; letter-spacing:.34em; text-align:center; color:${INK}; background:#fff; }
.oa-code:focus { outline:none; border-color:${BERRY}; }
.oa-done-mark { width:52px; height:52px; border-radius:999px; margin:16px 0 4px;
  display:flex; align-items:center; justify-content:center; color:#fff;
  animation:oaPop .45s cubic-bezier(.16,1,.3,1) both; }
.oa-done-mark--ok { background:#1F8A5B; box-shadow:0 0 0 0 rgba(31,138,91,.45); animation:oaPop .45s cubic-bezier(.16,1,.3,1) both, oaRing .9s ease-out .25s both; }
.oa-done-mark--wait { background:${BERRY}; }
/* Der Haken zeichnet sich, statt fertig dazustehen — 24 ist die Länge des Pfads auf dem
   lucide-Raster, großzügig aufgerundet. */
.oa-done-check path, .oa-done-check polyline { stroke-dasharray:30; stroke-dashoffset:30;
  animation:oaDraw .4s ease-out .2s forwards; }
@keyframes oaPop { from { transform:scale(.4); opacity:0 } to { transform:scale(1); opacity:1 } }
@keyframes oaRing { to { box-shadow:0 0 0 14px rgba(31,138,91,0) } }
@keyframes oaDraw { to { stroke-dashoffset:0 } }
/* Wer Bewegung abbestellt hat, bekommt dieselbe Marke ohne sie. */
@media (prefers-reduced-motion: reduce) {
  .oa-done-mark, .oa-done-mark--ok { animation:none }
  .oa-done-check path, .oa-done-check polyline { stroke-dashoffset:0; animation:none }
}
.oa-maybe { border:1px solid rgba(146,44,87,.22); background:rgba(146,44,87,.05); border-radius:12px;
  padding:12px; display:grid; gap:10px; }
.oa-maybe-head { font-family:${FONT_BODY}; font-weight:700; font-size:14px; color:${INK}; margin:0; }
.oa-grid2 { display:grid; grid-template-columns:120px 1fr; gap:10px; }
@media (max-width:560px) {
  .oa-backdrop { padding:0; align-items:flex-end; }
  .oa-dialog { max-width:none; max-height:96vh; border-radius:16px 16px 0 0; padding:18px 16px 24px; }
  .oa-title { font-size:20px; }
  .oa-grid2 { grid-template-columns:1fr; }
}
`;

/** Der auf der Angebotsseite gewählte Weg. Steuert Wortlaut, Tracking und das Ziel am Ende. */
export type AccessOffer = 'free' | 'premium' | 'promotion';

export type Step = 'organizer' | 'new' | 'identify' | 'code' | 'confirm' | 'done';

/** Wie der Weg endete. Jeder Ausgang hat genau einen nächsten Schritt. */
export type Outcome =
  | { kind: 'active'; organizerId: number; name: string }
  | { kind: 'manages'; organizerId: number; name: string }
  /**
   * `registered`: der Veranstalter ist neu und die Verwaltung hängt an derselben Freigabe.
   * `askedEmail`: an diese (maskierte) Vereinsadresse ist der Freischalt-Link gegangen.
   */
  | { kind: 'review'; name: string; registered?: boolean; askedEmail?: string };

interface Draft {
  name: string;
  website: string;
  email: string;
  postalCode: string;
  city: string;
}

/**
 * Punkt ans Satzende, aber nur einer: Vereinsnamen enden oft selbst auf einen („e.V."), und
 * „RSV Erbach 1912 e.V.." sieht aus wie ein Tippfehler.
 */
const satz = (text: string): string => (text.trimEnd().endsWith('.') ? text.trimEnd() : `${text.trimEnd()}.`);

const emptyDraft: Draft = { name: '', website: '', email: '', postalCode: '', city: '' };

interface Props {
  show: boolean;
  offer?: AccessOffer;
  /**
   * Schon gewählt: dann entfällt die Suche, und der Dialog beginnt bei der Anmeldung bzw. beim
   * Antrag. So kommt „Ich bin der Veranstalter" auf der Veranstalterseite zum selben Dialog,
   * statt einen zweiten zu haben — dort stand bis zum 22.09.2026 eine eigene Fassung mit
   * Freitextfeld.
   */
  organizer?: PublicOrganizer;
  onClose: () => void;
  /**
   * Nur für das Design-Lab: öffnet den Dialog mitten im Ablauf, damit alle Schritte nebeneinander
   * zu sehen sind. Die Anwendung setzt das nie, sie fängt immer bei der Suche an.
   */
  preview?: {
    step: Step;
    /** Suchbegriff und Treffer, damit das Lab die Auswahlliste ohne Backend zeigt. */
    query?: string;
    results?: PublicOrganizer[];
    /** Die „Meinst du vielleicht den?"-Vorschläge im Formular. */
    maybe?: PublicOrganizer[];
    organizer?: PublicOrganizer;
    draft?: Partial<Draft>;
    email?: string;
    note?: string;
    error?: string;
    outcome?: Outcome;
    /** Erzwingt den Zweig „Adresse passt" — nur für das Lab, die Anwendung rechnet ihn aus. */
    domainMatches?: boolean;
  };
}

/**
 * Sucht, sobald zwei Zeichen stehen, und wirft überholte Antworten weg: bei schnellem Tippen
 * kommt sonst die Antwort zum vorletzten Wort nach der zum letzten an.
 */
function useOrganizerSearch(query: string, active: boolean) {
  const [results, setResults] = useState<PublicOrganizer[]>([]);
  const [searching, setSearching] = useState(false);
  const runId = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (!active || q.length < 2) { setResults([]); setSearching(false); return; }
    const id = ++runId.current;
    setSearching(true);
    const timer = setTimeout(() => {
      api.get('/organizers', { params: { search: q } })
        .then(r => { if (id === runId.current) setResults((r.data as PublicOrganizer[]).slice(0, 8)); })
        .catch(() => { if (id === runId.current) setResults([]); })
        .finally(() => { if (id === runId.current) setSearching(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, active]);

  return { results, searching };
}

const Avatar: React.FC<{ name: string; logoUrl?: string }> = ({ name, logoUrl }) => (
  logoUrl
    ? <img className="oa-avatar" src={logoUrl} alt="" style={{ objectFit: 'contain', background: '#fff', border: `1px solid ${BORDER}` }} />
    : <span className="oa-avatar" style={{ background: organizerAvatarColor(name) }} aria-hidden>{organizerInitials(name)}</span>
);

/**
 * Der ganze Weg zum Veranstalter-Zugang in einem Dialog.
 *
 * Reihenfolge mit Absicht: erst der Veranstalter, dann die Anmeldung. Wer zuerst nach einer
 * E-Mail-Adresse gefragt wird, weiß noch nicht, wofür — und die Adresse ist genau dann die
 * richtige, wenn er den Verein schon vor Augen hat.
 */
const OrganizerAccessDialog: React.FC<Props> = ({ show, offer, organizer, onClose, preview }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, requestEmailCode, loginWithEmailCode } = useAuth();
  const { organizers: myOrganizers, reload: reloadPortal } = useOrganizerPortal();

  const [step, setStep] = useState<Step>('organizer');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<PublicOrganizer | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /**
   * Treffer, die zu dem passen, was gerade ins Formular getippt wird.
   *
   * Der Server fängt einen Doppeleintrag zwar beim Absenden ab, aber nur bei genau gleichem
   * Namen oder gleicher Domain. Ein Tippfehler („Kirchhelen") oder eine andere Schreibweise
   * („R.S.V.") rutscht durch, und der Verein steht danach zweimal im Verzeichnis. Also früher
   * fragen: sobald Name, Website oder Vereinsadresse dastehen.
   */
  const [maybe, setMaybe] = useState<PublicOrganizer[]>([]);
  const lookupRun = useRef(0);

  const live = useOrganizerSearch(query, show && step === 'organizer' && !preview?.results);
  const results = preview?.results ?? live.results;
  const searching = live.searching;
  /** Gesucht und nichts gefunden — daraus wird der Weg zum Neueintrag, nicht eine Fehlanzeige. */
  const noHits = query.trim().length >= 2 && !searching && results.length === 0;

  /** Name und Domain des gewählten Wegs — der neue Eintrag hat dieselben Felder wie ein vorhandener. */
  const targetName = picked?.name ?? draft.name.trim();
  const targetWebsite = picked?.website ?? draft.website;
  const targetEmail = picked ? undefined : draft.email;
  const domain = useMemo(() => claimDomainOf(targetWebsite), [targetWebsite]);
  const matchesLive = useMemo(
    () => organizerDomainMatches(user?.email, user?.emailVerified, targetWebsite, targetEmail),
    [user?.email, user?.emailVerified, targetWebsite, targetEmail],
  );
  // Das Lab hat kein angemeldetes Konto, muss den Fall „Adresse passt" aber zeigen können — er
  // sieht anders aus als der Antrag daneben (Freischalten statt Beantragen).
  const domainMatches = preview?.domainMatches ?? matchesLive;

  useEffect(() => {
    // Im Design-Lab stehen mehrere Schritte nebeneinander auf einer Seite. Würde jeder davon die
    // Seite sperren, ließe sich der Eintrag weder scrollen noch vollständig fotografieren.
    if (!show || preview) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('modal-open');
    };
    // Beim Öffnen einmal: ein Wechsel des Anmeldezustands darf den Dialog nicht mitten im
    // Ablauf zurücksetzen — genau das täte er, stünde `isAuthenticated` in der Liste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, preview, organizer]);

  // Beim Öffnen auf Anfang: ein Dialog, der den halb ausgefüllten Stand von vorhin zeigt, ist
  // nicht hilfreich, sondern verwirrend.
  useEffect(() => {
    if (!show) return;
    setStep(preview?.step ?? (organizer ? (isAuthenticated ? 'confirm' : 'identify') : 'organizer'));
    setQuery(preview?.query ?? '');
    setPicked(preview?.organizer ?? organizer ?? null);
    setDraft({ ...emptyDraft, ...preview?.draft });
    setEmail(preview?.email ?? '');
    setCode('');
    setNote(preview?.note ?? '');
    setError(preview?.error ?? null);
    setOutcome(preview?.outcome ?? null);
    // Beim Öffnen einmal: ein Wechsel des Anmeldezustands darf den Dialog nicht mitten im
    // Ablauf zurücksetzen — genau das täte er, stünde `isAuthenticated` in der Liste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, preview, organizer]);

  useEffect(() => {
    if (step !== 'new' || preview) { setMaybe(preview?.maybe ?? []); return; }
    const name = draft.name.trim();
    const website = draft.website.trim();
    const email = draft.email.trim();
    if (name.length < 3 && !website && !email) { setMaybe([]); return; }
    const id = ++lookupRun.current;
    const timer = setTimeout(() => {
      organizerAccessService.lookup({
        name: name || undefined, website: website || undefined, email: email || undefined,
      })
        .then(hits => { if (id === lookupRun.current) setMaybe(hits); })
        .catch(() => { if (id === lookupRun.current) setMaybe([]); });
    }, 400);
    return () => clearTimeout(timer);
  }, [step, draft.name, draft.website, draft.email, preview]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const close = useCallback(() => { onClose(); }, [onClose]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, close]);

  /** Vorhandener Veranstalter: anmelden, falls nötig, sonst direkt zum Antrag. */
  const afterTarget = (organizerId: number) => {
    trackOrganizerFunnel('claim_started', { offer, organizerId });
    setStep(isAuthenticated ? 'confirm' : 'identify');
  };

  const pickOrganizer = (organizer: PublicOrganizer) => {
    trackOrganizerFunnel('organizer_selected', { offer, organizerId: organizer.id });
    setPicked(organizer);
    setError(null);
    // Schon verknüpft: dann ist nichts zu beantragen, der Weg führt direkt in den Bereich.
    if (myOrganizers.some(o => o.id === organizer.id)) {
      setOutcome({ kind: 'manages', organizerId: organizer.id, name: organizer.name });
      setStep('done');
      return;
    }
    afterTarget(organizer.id);
  };

  const startNew = () => {
    setPicked(null);
    setDraft({ ...emptyDraft, name: query.trim() });
    setError(null);
    setStep('new');
  };

  /**
   * Ein neuer Veranstalter braucht keine Bestätigungsseite: das Formular *ist* die Angabe, und
   * die Bewerbung als Verwalter hängt am selben Vorgang (`claim_access`). Wer angemeldet ist,
   * trägt hier ein; wer nicht, gibt erst seine Adresse an und es geht danach von selbst weiter.
   */
  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (draft.name.trim().length < 3) {
      setError('Bitte gib den Namen des Veranstalters an.');
      return;
    }
    setError(null);
    trackOrganizerFunnel('claim_started', { offer });
    if (isAuthenticated) submitAccess();
    else setStep('identify');
  };

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const address = email.trim();
    if (!address.includes('@') || address.length < 5) {
      setError('Bitte gib deine E-Mail-Adresse an.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestEmailCode(address);
      setStep('code');
      setCode('');
      setCooldown(60);
    } catch {
      setError('Der Code konnte nicht gesendet werden. Bitte versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value: string) => {
    setBusy(true);
    setError(null);
    try {
      // Danach folgt hier noch der Antrag; die Frage nach Geburtsjahr und Radarten stünde im Weg.
      await loginWithEmailCode(email.trim(), value, undefined, { askDetails: false });
      reloadPortal();
      // Beim Neueintrag steht schon alles im Formular — ein Bestätigungsschritt fragte nichts
      // mehr, er wäre nur ein weiterer Klick.
      if (picked) setStep('confirm');
      else await submitAccess();
    } catch {
      setError('Der Code stimmt nicht oder ist abgelaufen. Fordere einen neuen an.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const onCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6 && !busy) submitCode(digits);
  };

  /** Der letzte Schritt: Antrag auf einen vorhandenen Veranstalter oder ein neuer Eintrag. */
  const submitAccess = async () => {
    setBusy(true);
    setError(null);
    try {
      if (picked) {
        const member = await organizerAccessService.request({
          organizerId: picked.id, note: note.trim() || undefined,
        });
        const active = member.status === 'ACTIVE';
        trackOrganizerFunnel('claim_completed', {
          offer, organizerId: picked.id, result: active ? 'automatic' : 'review',
        });
        reloadPortal();
        setOutcome(active
          ? { kind: 'active', organizerId: picked.id, name: picked.name }
          // Wen wir gefragt haben, sagt die Antwort auf den Antrag selbst — eine zweite Abfrage
          // vorher wäre ein Rennen: wer schnell klickt, bekäme die falsche Auskunft.
          : { kind: 'review', name: picked.name, askedEmail: member.askedEmail });
        setStep('done');
        return;
      }
      const created = await organizerAccessService.register({
        name: draft.name.trim(),
        website: draft.website.trim() || undefined,
        email: draft.email.trim() || undefined,
        postalCode: draft.postalCode.trim() || undefined,
        city: draft.city.trim() || undefined,
        note: note.trim() || undefined,
      });
      trackOrganizerFunnel('organizer_registration_submitted', {
        offer,
        result: created.organizerId != null ? 'automatic'
          : created.existingOrganizerId != null ? 'existing' : 'review',
      });
      reloadPortal();
      // Den gibt es schon: dann ist der Antrag dort der richtige Weg, nicht ein zweiter Eintrag.
      if (created.existingOrganizerId != null) {
        const existing: PublicOrganizer = {
          id: created.existingOrganizerId,
          name: created.existingOrganizerName ?? draft.name.trim(),
          active: true,
        };
        setPicked(existing);
        setDraft(emptyDraft);
        setError(`${existing.name} steht schon bei uns. Beantrage den Zugang zu diesem Eintrag.`);
        setStep('confirm');
        return;
      }
      setOutcome(created.organizerId != null
        ? { kind: 'active', organizerId: created.organizerId, name: draft.name.trim() }
        : { kind: 'review', name: draft.name.trim(), registered: true });
      setStep('done');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  };

  const goToOrganizer = (organizerId: number) => {
    // Der gewählte Weg endet nicht am Zugang: Premium öffnet auf der Veranstalterseite den
    // Checkout, die Hervorhebung den passenden Reiter (siehe OrganizerDetail, `offerIntent`).
    const suffix = offer === 'premium' || offer === 'promotion' ? `?angebot=${offer}` : '';
    close();
    navigate(`/veranstalter/${organizerId}${suffix}`);
  };

  if (!show) return null;

  // Benannte Schritte statt „Schritt 2 von 3": der Weg ist verschieden lang — angemeldet
  // entfällt die Adresse, beim Neueintrag der Antrag. Eine Zahl, die nicht stimmt, ist
  // schlimmer als keine.
  const stepLabel = step === 'organizer' ? 'Veranstalter'
    : step === 'new' ? 'Neuer Veranstalter'
    : step === 'identify' ? 'Deine Adresse'
    : step === 'code' ? 'Code aus der Mail'
    : step === 'confirm' ? (domainMatches === true ? 'Freischalten' : 'Antrag') : 'Fertig';

  const back = step === 'new' ? () => setStep('organizer')
    : step === 'identify' ? (organizer ? null : () => setStep(picked ? 'organizer' : 'new'))
    : step === 'code' ? () => setStep('identify')
    : step === 'confirm' && !isAuthenticated ? () => setStep('code')
    : step === 'confirm' ? (organizer ? null : () => setStep('organizer'))
    : null;

  return (
    <div className="oa-backdrop" role="dialog" aria-modal="true" aria-label="Veranstalter-Zugang"
      onClick={e => { if (e.target === e.currentTarget) close(); }}>
      <style>{ORGANIZER_ACCESS_CSS}</style>
      <div className="oa-dialog">
        <div className="oa-top">
          {/* Der Platz des Zurück-Knopfes bleibt auch im ersten Schritt stehen. Sonst rutscht der
              Schrittname von der Mitte nach links, sobald es kein Zurück gibt, und die Kopfzeile
              springt beim Weiterklicken. */}
          {back
            ? <button type="button" className="oa-icon-btn" onClick={back} aria-label="Zurück"><ArrowLeft size={16} /></button>
            : <span className="oa-icon-slot" aria-hidden />}
          <span className="oa-step">{stepLabel}</span>
          <button type="button" className="oa-icon-btn" onClick={close} aria-label="Schließen"><X size={16} /></button>
        </div>

        {step === 'organizer' && (
          <>
            <h2 className="oa-title">Welchen Veranstalter möchtest du verwalten?</h2>
            {/* Kein Einleitungssatz: die Frage steht in der Überschrift, das Feld darunter ist
                die Antwort. Alles dazwischen hält nur auf. */}
            <div className="oa-body">
              <div className="oa-search">
                <Search size={16} color="#9A9494" aria-hidden />
                <input value={query} onChange={e => setQuery(e.target.value)} autoFocus
                  placeholder="Name des Vereins oder Anbieters" aria-label="Veranstalter suchen" />
              </div>

              {query.trim().length < 2 ? (
                <p className="oa-hint">Gib zwei Buchstaben ein, dann zeigen wir passende Einträge.</p>
              ) : searching && results.length === 0 ? (
                <p className="oa-hint">Wird gesucht…</p>
              ) : results.length === 0 ? (
                /* Nichts gefunden heißt hier nicht „Fehlanzeige", sondern „dann legen wir ihn an":
                   ein Knopf, der den eingetippten Namen mitnimmt. Eine Meldung plus ein zweiter
                   Verweis darunter wären zwei Wege zum selben Ziel. */
                <div className="oa-actions">
                  <button type="button" className="oa-btn oa-btn--primary" onClick={startNew}>
                    <Plus size={16} aria-hidden />
                    „{query.trim()}" eintragen
                  </button>
                </div>
              ) : (
                <div className="oa-results">
                  {results.map(o => (
                    <button type="button" key={o.id} className="oa-result" onClick={() => pickOrganizer(o)}>
                      <Avatar name={o.name} logoUrl={o.logoUrl} />
                      <span style={{ minWidth: 0 }}>
                        <span className="oa-result-name">{o.name}</span>
                        {/* Ohne Ort und ohne Betreuung bleibt die Zeile weg: „Ohne Ortsangabe"
                            sagt nichts über den Verein, es füllt nur Platz. */}
                        {(o.city || o.verified) && (
                          <span className="oa-result-sub">
                            {[o.city, o.verified ? 'wird schon betreut' : null].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Nur solange die Liste noch etwas zeigt: im Leerfall steht der Weg schon oben. */}
              {!noHits && (
                <div className="oa-missing">
                  Veranstalter noch nicht dabei?{' '}
                  <button type="button" className="oa-link" onClick={startNew}>Neu eintragen</button>
                </div>
              )}
            </div>
          </>
        )}

        {step === 'new' && (
          <>
            <h2 className="oa-title">Veranstalter eintragen</h2>
            <p className="oa-lead">
              Wir legen ihn an und tragen dich als Verwalter ein. Nur der Name ist Pflicht,
              Website und Vereinsadresse helfen uns beim Freischalten.
            </p>
            <form className="oa-body" onSubmit={submitNew}>
              {/* „Meinst du vielleicht den?" — steht über den Feldern, weil es die Arbeit spart,
                  die darunter noch käme. Ein Klick führt in den Antrag auf den vorhandenen
                  Eintrag, also genau dorthin, wo derjenige hinwollte. */}
              {maybe.length > 0 && (
                <div className="oa-maybe">
                  <p className="oa-maybe-head">
                    {maybe.length === 1 ? 'Meinst du vielleicht den hier?' : 'Meinst du vielleicht einen davon?'}
                  </p>
                  <div className="oa-results">
                    {maybe.map(o => (
                      <button type="button" key={o.id} className="oa-result" onClick={() => pickOrganizer(o)}>
                        <Avatar name={o.name} logoUrl={o.logoUrl} />
                        <span style={{ minWidth: 0 }}>
                          <span className="oa-result-name">{o.name}</span>
                          {(o.city || o.website) && (
                            <span className="oa-result-sub">{o.city ?? claimDomainOf(o.website)}</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <label>
                <span className="oa-label">Name des Veranstalters</span>
                <input className="oa-input" value={draft.name} autoFocus required
                  onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                  placeholder="RSV Beispiel e.V. oder Beispiel Events GmbH" />
              </label>
              <label>
                <span className="oa-label">Website</span>
                <input className="oa-input" value={draft.website} inputMode="url"
                  onChange={e => setDraft(d => ({ ...d, website: e.target.value }))}
                  placeholder="https://www.rsv-beispiel.de" />
              </label>
              <label>
                <span className="oa-label">E-Mail des Veranstalters</span>
                <input className="oa-input" value={draft.email} type="email"
                  onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
                  placeholder="info@rsv-beispiel.de" />
              </label>
              <div className="oa-grid2">
                <label>
                  <span className="oa-label">PLZ</span>
                  <input className="oa-input" value={draft.postalCode} inputMode="numeric"
                    onChange={e => setDraft(d => ({ ...d, postalCode: e.target.value }))} />
                </label>
                <label>
                  <span className="oa-label">Ort</span>
                  <input className="oa-input" value={draft.city}
                    onChange={e => setDraft(d => ({ ...d, city: e.target.value }))} />
                </label>
              </div>
              {error && <p className="oa-hint" style={{ color: '#8C3B0B' }}>{error}</p>}
              <div className="oa-actions">
                <button type="submit" className="oa-btn oa-btn--primary" disabled={busy}>
                  {busy ? 'Wird eingetragen…' : isAuthenticated ? 'Eintragen und übernehmen' : 'Weiter'}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'identify' && (
          <>
            <h2 className="oa-title">Wie erreichen wir dich?</h2>
            <p className="oa-lead">
              Gib deine E-Mail-Adresse an. Hast du schon ein Konto bei uns, melden wir dich damit
              an. Wenn nicht, legen wir eines für dich an.
            </p>
            <form className="oa-body" onSubmit={sendCode}>
              <div className="oa-picked">
                <Avatar name={targetName} logoUrl={picked?.logoUrl} />
                <span style={{ minWidth: 0 }}>
                  <span className="oa-result-name">{targetName}</span>
                  <span className="oa-result-sub">{picked ? 'Dein Veranstalter' : 'Wird neu eingetragen'}</span>
                </span>
              </div>
              <label>
                <span className="oa-label">Deine E-Mail-Adresse</span>
                <input className="oa-input" value={email} type="email" autoFocus required
                  onChange={e => setEmail(e.target.value)}
                  placeholder={domain ? `du@${domain}` : 'du@verein.de'} />
              </label>
              <p className="oa-hint">
                {domain
                  ? <>Nimm am besten deine Adresse auf <strong>@{domain}</strong>. Dann ist der Zugang sofort da,
                      sonst fragen wir kurz beim Veranstalter nach.</>
                  : <>Nimm am besten deine Adresse beim Veranstalter. Passt sie zur Domain, ist der Zugang
                      sofort da, sonst fragen wir dort kurz nach.</>}
              </p>
              <p className="oa-hint"><PrivacyNote /></p>
              {error && <p className="oa-hint" style={{ color: '#8C3B0B' }}>{error}</p>}
              <div className="oa-actions">
                <button type="submit" className="oa-btn oa-btn--primary" disabled={busy}>
                  {busy ? 'Wird gesendet…' : 'Code anfordern'}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <h2 className="oa-title">Code eingeben</h2>
            <p className="oa-lead">
              Wir haben dir einen sechsstelligen Code an <strong>{email.trim()}</strong> geschickt.
              Er gilt zehn Minuten.
            </p>
            <div className="oa-body">
              <input className="oa-code" value={code} inputMode="numeric" autoComplete="one-time-code"
                autoFocus maxLength={6} aria-label="Sechsstelliger Code"
                onChange={e => onCodeChange(e.target.value)} placeholder="······" />
              {error && <p className="oa-hint" style={{ color: '#8C3B0B' }}>{error}</p>}
              <p className="oa-hint">
                Keine Mail bekommen? Sieh im Spam-Ordner nach.{' '}
                {cooldown > 0
                  ? <>Neuer Code in {cooldown} Sekunden.</>
                  : <button type="button" className="oa-link" onClick={() => sendCode()}>Code erneut senden</button>}
              </p>
              <p className="oa-hint">
                Du gehörst zur Redaktion? Dann{' '}
                <button type="button" className="oa-link"
                  onClick={() => { close(); navigate('/login?redirect=%2Ffuer-veranstalter'); }}>
                  melde dich mit Passwort an
                </button>.
              </p>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            {/* „Beantragen" wäre falsch, wenn der Server gleich freischaltet: Schrittname, Titel
                und Knopf sagen deshalb dasselbe, je nachdem was gleich passiert. */}
            <h2 className="oa-title">{domainMatches === true ? 'Zugang freischalten' : 'Zugang beantragen'}</h2>
            <p className="oa-lead">
              Du übernimmst die Seite von <strong>{targetName}</strong> und pflegst Stammdaten und
              Events selbst.
            </p>
            <div className="oa-body">
              {/* Nur die gute Nachricht steht hier. Was bei nicht passender Adresse als Nächstes
                  passiert, sagt der Abschluss — vorher stand derselbe Satz zweimal, und auf dem
                  Weg zum Knopf hält er nur auf. Kein Freitextfeld mehr: „Ich bin der Vorsitzende"
                  konnte niemand prüfen, den Nachweis führt jetzt die Domain oder der Verein. */}
              {domainMatches === true && (
                <p className="oa-note">
                  Deine Adresse {user?.email} passt zum Veranstalter. Der Zugang ist sofort da.
                </p>
              )}
              {error && <p className="oa-hint" style={{ color: '#8C3B0B' }}>{error}</p>}
              <div className="oa-actions">
                <button type="button" className="oa-btn oa-btn--primary" onClick={submitAccess} disabled={busy}>
                  {busy ? 'Einen Moment…' : domainMatches === true ? 'Zugang freischalten' : 'Zugang beantragen'}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'done' && outcome && (
          <>
            {/* Der Abschluss ist der Moment, auf den alles zulief: er bekommt eine Marke, die
                aufpoppt, und beim Haken zeichnet sich der Strich nach.

                Aber nicht überall derselbe: grün und Haken heißt „du bist drin". Ein Antrag, der
                noch auf den Verein wartet, trägt den Umschlag in Markenfarbe — dieselbe Größe,
                dieselbe Bewegung, nur ohne das Versprechen, das er nicht halten kann. Ein grüner
                Haken über „wir haben nachgefragt" hieße für den Leser: fertig. */}
            <div className={`oa-done-mark ${outcome.kind === 'review' ? 'oa-done-mark--wait' : 'oa-done-mark--ok'}`} aria-hidden>
              {outcome.kind === 'review'
                ? (outcome.askedEmail ? <Mail size={22} /> : <Clock size={22} />)
                : <Check className="oa-done-check" size={26} />}
            </div>
            <h2 className="oa-title">
              {outcome.kind === 'review'
                ? outcome.registered
                  ? satz(`${outcome.name} ist eingereicht`)
                  : outcome.askedEmail
                    ? 'Wir haben beim Veranstalter nachgefragt.'
                    : satz(`Wir prüfen deinen Antrag für ${outcome.name}`)
                : outcome.kind === 'manages'
                  ? satz(`Du verwaltest ${outcome.name} schon`)
                  : 'Zugang freigeschaltet.'}
            </h2>
            <p className="oa-lead">
              {outcome.kind === 'review'
                ? outcome.registered
                  ? 'Jemand von uns sieht sich die Angaben an, meist noch am selben Tag. Mit der Freigabe steht der Veranstalter im Verzeichnis und du verwaltest ihn. Wir schreiben dir, sobald es so weit ist.'
                  : outcome.askedEmail
                    ? `Eine Mail an ${outcome.askedEmail} ist unterwegs. Ein Klick auf den Link darin schaltet dich frei, er gilt 96 Stunden.`
                    : 'Jemand von uns sieht sich den Antrag an, meist noch am selben Tag. Danach bekommst du eine Mail und kannst loslegen.'
                : outcome.kind === 'manages'
                  ? 'Weiter geht es auf der Veranstalterseite: Stammdaten, Logo und deine Events.'
                  : `Deine Adresse passte zu ${outcome.name}, deshalb musste niemand prüfen. Vervollständige jetzt die Stammdaten und trag dein erstes Event ein.`}
            </p>
            <div className="oa-body">
              <div className="oa-actions">
                {outcome.kind === 'review' ? (
                  <button type="button" className="oa-btn oa-btn--primary" onClick={close}>Alles klar</button>
                ) : (
                  <button type="button" className="oa-btn oa-btn--primary"
                    onClick={() => goToOrganizer(outcome.organizerId)}>
                    Zur Veranstalterseite
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrganizerAccessDialog;
